import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { getNativePlatform } from "@/lib/runtime";
import { NativeSettingsSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";
import {
  useAccount,
  useChangePassword,
  useSessions,
  useRevokeSession,
  useRevokeAllOtherSessions,
  useStartEnable2FA,
  useVerify2FA,
  useDisable2FA,
} from "@/queries/account";
import { useAuth } from "@/contexts/AuthContext";
import { toDate } from "@/utils/date";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
  InputOTPSeparator,
} from "@/components/ui/input-otp";
import { useHaptics } from "@/haptics/HapticsProvider";
import { CheckCircle2, Copy, Eye, EyeOff, KeyRound, Monitor, RotateCcw, Shield, Smartphone } from "lucide-react";
import PasswordRequirements from "@/components/PasswordRequirements";
import { isPasswordValid, PASSWORD_POLICY_MESSAGE } from "@/lib/passwordPolicy";

const securityInputClassName =
  "h-11 rounded-xl border-border/70 bg-muted/30 px-4 shadow-none placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20";

function prettyCH(ch) {
  if (!ch) return "";
  const re = /"([^"]+)";v="(\d+)"/g;
  let match;
  let best = null;
  while ((match = re.exec(ch))) {
    const name = match[1];
    const ver = match[2];
    if (/not.?a.?brand/i.test(name)) continue;
    const score =
      /duckduckgo|opera|edge|chrome/i.test(name) ? 2 :
      /chromium/i.test(name) ? 1 :
      0;
    if (!best || score > best.score) best = { label: `${name} ${ver}`, score };
  }
  return best ? best.label : "";
}

function guessFromUA(ua = "") {
  if (/edg/i.test(ua)) return "Microsoft Edge";
  if (/opr|opera/i.test(ua)) return "Opera";
  if (/duckduckgo/i.test(ua)) return "DuckDuckGo";
  if (/chrome/i.test(ua)) return "Chrome";
  if (/safari/i.test(ua)) return "Safari";
  if (/firefox/i.test(ua)) return "Firefox";
  return "";
}

function prettyBrowser(session) {
  return (
    session.browser_nice ||
    session.browserNice ||
    prettyCH(session.browser) ||
    guessFromUA(session.user_agent) ||
    "Okänd webbläsare"
  );
}

function prettyOS(session) {
  if (session.os_nice) return session.os_nice;
  if (session.osNice) return session.osNice;
  if (session.os && typeof session.os === "string") {
    const value = session.os.replace(/"/g, "").trim();
    if (/^(android|ios|windows|macos|linux)$/i.test(value)) return value;
  }
  const ua = session.user_agent || "";
  if (/android/i.test(ua)) return "Android";
  if (/iphone|ipad|ios/i.test(ua)) return "iOS";
  if (/windows/i.test(ua)) return "Windows";
  if (/mac os|macintosh/i.test(ua)) return "macOS";
  if (/linux/i.test(ua)) return "Linux";
  return "Okänt OS";
}

function deviceLabel(session) {
  const raw = String(session.device_label || session.device || session.device_type || "").trim();
  const value = `${raw} ${session.user_agent || ""}`.toLowerCase();
  if (/iphone/.test(value)) return "iPhone";
  if (/ipad/.test(value)) return "iPad";
  if (/android|mobile|telefon/.test(value)) return "Android-enhet";
  if (/windows/.test(value)) return "Windows-dator";
  if (/mac os|macintosh|macos/.test(value)) return "Mac";
  if (/linux/.test(value)) return "Linux-dator";
  if (/desktop|computer|dator/.test(value)) return "Dator";
  return "Okänd enhet";
}

function formatSessionDateTime(value) {
  const dt = toDate(value);
  if (!dt || Number.isNaN(dt.getTime())) return "Okänd tid";
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dt);
}

function SessionDeviceIcon({ session }) {
  const value = `${session.device_type || ""} ${session.device || ""} ${session.device_label || ""} ${session.user_agent || ""}`.toLowerCase();
  const isMobile = /iphone|ipad|android|telefon|mobile|apple-enhet/.test(value);
  const Icon = isMobile ? Smartphone : Monitor;
  return <Icon className="mt-0.5 h-4 w-4 shrink-0 opacity-70" />;
}

export default function SecurityPanel() {
  const { logout } = useAuth() || {};
  const { data: account, isLoading: accountLoading } = useAccount();
  const { hash } = useLocation();

  const h = useHaptics();
  const prevLenRef = useRef(0);
  const twoFactorSectionRef = useRef(null);

  const changePwd = useChangePassword();
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    next: false,
  });
  const passwordDirty = Boolean(pwd.current_password || pwd.new_password);
  const passwordReady = Boolean(pwd.current_password && isPasswordValid(pwd.new_password));
  const onPwd = (key) => (e) => setPwd((prev) => ({ ...prev, [key]: e.target.value }));

  const savePwd = async () => {
    if (!isPasswordValid(pwd.new_password)) {
      toast.error("Lösenordet uppfyller inte kraven", { description: PASSWORD_POLICY_MESSAGE });
      return;
    }
    await toast.promise(changePwd.mutateAsync(pwd), {
      loading: "Byter…",
      success: "Lösenord uppdaterat",
      error: "Kunde inte byta lösenord. Kontrollera ditt nuvarande lösenord och försök igen.",
    });
    setPwd({ current_password: "", new_password: "" });
    setShowPasswords({ current: false, next: false });
  };

  const resetPwd = () => {
    setPwd({ current_password: "", new_password: "" });
    setShowPasswords({ current: false, next: false });
  };

  useEffect(() => {
    if (hash !== "#twofa") return;
    twoFactorSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [show2FASecret, setShow2FASecret] = useState(false);
  const enable2fa = useStartEnable2FA();
  const verify2fa = useVerify2FA();
  const disable2fa = useDisable2FA();

  const begin2FA = async () => {
    const nextSetup = await enable2fa.mutateAsync();
    setSetup(nextSetup);
    setShow2FASecret(false);
  };

  const confirm2FA = async () => {
    const sanitized = (code || "").replace(/\D/g, "");
    if (sanitized.length !== 6) {
      h.error();
      toast.error("Ange 6 siffror");
      return;
    }
    if (verify2fa.isPending) return;

    try {
      await toast.promise(verify2fa.mutateAsync(sanitized), {
        loading: "Aktiverar…",
        success: "2FA aktiverad",
        error: "Fel kod",
      });
      h.success();
      setSetup(null);
      setCode("");
      setShow2FASecret(false);
      prevLenRef.current = 0;
    } catch {
      h.error();
    }
  };

  const turnOff2FA = async () => {
    await toast.promise(disable2fa.mutateAsync(), {
      loading: "Stänger av…",
      success: "2FA avstängd",
      error: "Kunde inte stänga av",
    });
  };

  const copy2FASecret = async () => {
    const secret = setup?.secret;
    if (!secret) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(secret);
      } else {
        const el = document.createElement("textarea");
        el.value = secret;
        el.setAttribute("readonly", "");
        el.style.position = "absolute";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      toast.success("2FA-nyckeln kopierad");
      h.tap();
    } catch {
      toast.error("Kunde inte kopiera 2FA-nyckeln");
      h.error();
    }
  };

  const { data: sessions = [], isLoading: sessionsLoading } = useSessions();
  const revoke = useRevokeSession();
  const revokeAllOther = useRevokeAllOtherSessions();

  const onRevokeOne = async (session) => {
    await toast.promise(revoke.mutateAsync(session.id), {
      loading: "Loggar ut…",
      success: "Session utloggad",
      error: "Kunde inte logga ut sessionen",
    });
    if (session.current && typeof logout === "function") {
      logout();
    }
  };

  const hasOtherSessions = useMemo(() => sessions.some((session) => !session.current), [sessions]);

  if ((accountLoading || sessionsLoading) && getNativePlatform() === "android") {
    return <NativeSettingsSkeleton sections={3} />;
  }

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 shrink-0 opacity-70" />
            <span>Byt lösenord</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3">
          <div className="rounded-2xl border bg-card/60 p-4">
            <label className="flex items-center gap-2 text-sm font-medium">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              Nuvarande lösenord
            </label>
            <div className="relative mt-2">
              <Input
                className={`${securityInputClassName} pr-11`}
                type={showPasswords.current ? "text" : "password"}
                placeholder="Ange ditt nuvarande lösenord"
                value={pwd.current_password}
                onChange={onPwd("current_password")}
                autoComplete="current-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1 h-9 w-9 text-muted-foreground"
                onClick={() => setShowPasswords((prev) => ({ ...prev, current: !prev.current }))}
                aria-label={showPasswords.current ? "Dölj lösenord" : "Visa lösenord"}
              >
                {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border bg-card/60 p-4">
            <label className="flex items-center gap-2 text-sm font-medium">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
              Nytt lösenord
            </label>
            <div className="relative mt-2">
              <Input
                className={`${securityInputClassName} pr-11`}
                type={showPasswords.next ? "text" : "password"}
                placeholder="Välj ett nytt lösenord"
                value={pwd.new_password}
                onChange={onPwd("new_password")}
                autoComplete="new-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1 h-9 w-9 text-muted-foreground"
                onClick={() => setShowPasswords((prev) => ({ ...prev, next: !prev.next }))}
                aria-label={showPasswords.next ? "Dölj lösenord" : "Visa lösenord"}
              >
                {showPasswords.next ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Använd ett lösenord du inte använder någon annanstans.
            </p>
            <PasswordRequirements password={pwd.new_password} />
          </div>

          <div className="flex justify-end gap-2">
            {passwordDirty ? (
              <Button type="button" variant="ghost" onClick={resetPwd}>
                <RotateCcw className="h-4 w-4" />
                Ångra
              </Button>
            ) : null}
            <Button onClick={savePwd} disabled={changePwd.isPending || !passwordReady}>
              Uppdatera
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card id="twofa" ref={twoFactorSectionRef} className="native-v3-security-2fa">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 shrink-0 opacity-70" />
            <span>Tvåstegsverifiering</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3">
          {account?.two_factor_enabled ? (
            <div className="native-v3-security-status" data-enabled="true">
              <span className="native-v3-security-status-icon"><CheckCircle2 aria-hidden="true" /></span>
              <span className="native-v3-security-status-copy">
                <strong>Extra skydd är aktivt</strong>
                <small>En verifieringskod krävs när du loggar in.</small>
              </span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={disable2fa.isPending}>
                    Stäng av
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Stäng av tvåstegsverifiering?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Ditt konto blir mindre skyddat om du stänger av 2FA.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Avbryt</AlertDialogCancel>
                    <AlertDialogAction onClick={turnOff2FA}>
                      Stäng av 2FA
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ) : setup ? (
            <div className="native-v3-security-setup grid gap-4">
              <div className="native-v3-security-setup-heading">
                <span>1</span>
                <div><strong>Koppla en autentiseringsapp</strong><small>Skanna koden eller använd den manuella nyckeln.</small></div>
              </div>
              <div className="grid gap-2 text-sm text-muted-foreground">
                <p>Öppna din autentiseringsapp och lägg till ett nytt konto.</p>
                <p>Skanna QR-koden med en annan enhet, eller ange nyckeln manuellt i samma telefon.</p>
              </div>
              {setup.qrcodeDataUrl ? (
                <img src={setup.qrcodeDataUrl} alt="Skanna i din Auth-app" className="h-40 w-40" />
              ) : (
                <code className="native-v3-security-fallback-key">
                  {setup.otpauth_url || setup.secret}
                </code>
              )}

              {setup.secret ? (
                <div className="native-v3-security-manual-key">
                  <div className="native-v3-security-manual-key-header">
                    <div><strong>Manuell nyckel</strong><small>Använd om QR-koden inte kan skannas.</small></div>
                    <div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setShow2FASecret((prev) => !prev)}
                        aria-label={show2FASecret ? "Dölj nyckel" : "Visa nyckel"}
                      >
                        {show2FASecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={copy2FASecret}
                        aria-label="Kopiera nyckel"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <code>
                    {show2FASecret ? setup.secret : "•••• •••• •••• ••••"}
                  </code>
                </div>
              ) : null}

              <div className="grid gap-2">
                <div className="native-v3-security-setup-heading">
                  <span>2</span>
                  <div><strong>Bekräfta anslutningen</strong><small>Ange den sexsiffriga koden från appen.</small></div>
                </div>
                <label className="text-sm text-muted-foreground">Verifieringskod (6 siffror)</label>
                <InputOTP
                  containerClassName="native-v3-security-otp"
                  maxLength={6}
                  autoFocus
                  inputMode="numeric"
                  pattern="\d*"
                  value={code}
                  onChange={(value) => {
                    const clean = value.replace(/\D/g, "").slice(0, 6);
                    if (clean.length > prevLenRef.current) {
                      h.tap();
                    }
                    prevLenRef.current = clean.length;
                    setCode(clean);
                  }}
                  onComplete={() => {
                    if (!verify2fa.isPending) confirm2FA();
                  }}
                >
                  <InputOTPGroup className="native-v3-security-otp-group">
                    <InputOTPSlot className="native-v3-security-otp-slot" index={0} />
                    <InputOTPSlot className="native-v3-security-otp-slot" index={1} />
                    <InputOTPSlot className="native-v3-security-otp-slot" index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator className="native-v3-security-otp-separator" />
                  <InputOTPGroup className="native-v3-security-otp-group">
                    <InputOTPSlot className="native-v3-security-otp-slot" index={3} />
                    <InputOTPSlot className="native-v3-security-otp-slot" index={4} />
                    <InputOTPSlot className="native-v3-security-otp-slot" index={5} />
                  </InputOTPGroup>
                </InputOTP>

                <div className="native-v3-security-confirm-action">
                  <Button
                    onClick={() => h.tap()}
                    disabled={verify2fa.isPending || (code || "").replace(/\D/g, "").length !== 6}
                  >
                    Bekräfta
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="native-v3-security-status" data-enabled="false">
              <span className="native-v3-security-status-icon"><Shield aria-hidden="true" /></span>
              <span className="native-v3-security-status-copy">
                <strong>Tvåstegsverifiering är av</strong>
                <small>Lägg till ett extra skydd vid inloggning.</small>
              </span>
              <Button onClick={begin2FA} disabled={enable2fa.isPending}>
                Aktivera
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="native-v3-security-sessions">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 shrink-0 opacity-70" />
            <span>Aktiva sessioner</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3">
          {sessions.length === 0 ? (
            <div className="text-muted-foreground">Inga sessioner</div>
          ) : (
            sessions.map((session) => {
              const browser = prettyBrowser(session);
              const os = prettyOS(session);
              const dev = deviceLabel(session);
              return (
                <div key={session.id} className="native-v3-session-row" data-current={session.current ? "true" : "false"}>
                  <span className="native-v3-session-device"><SessionDeviceIcon session={session} /></span>
                  <div className="native-v3-session-copy">
                    <div>
                      <strong>{dev}</strong>
                      {session.current ? (
                        <span className="native-v3-session-current">
                          Den här enheten
                        </span>
                      ) : null}
                    </div>
                    <small>{browser} · {os}</small>
                    <small>Senast aktiv {formatSessionDateTime(session.last_active)}</small>
                  </div>
                  <Button
                    className="native-v3-session-logout"
                    variant={session.current ? "destructive" : "outline"}
                    size="sm"
                    onClick={() => onRevokeOne(session)}
                    disabled={revoke.isPending}
                    title={session.current ? "Du loggas ut direkt" : "Logga ut denna enhet"}
                  >
                    Logga ut
                  </Button>
                </div>
              );
            })
          )}

          {hasOtherSessions ? (
            <div className="native-v3-session-all-action">
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  toast.promise(revokeAllOther.mutateAsync(), {
                    loading: "Loggar ut från andra enheter…",
                    success: "Utloggad från andra enheter",
                    error: "Misslyckades logga ut andra enheter",
                  })
                }
                disabled={revokeAllOther.isPending}
              >
                Logga ut från andra enheter
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
